import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { appendUpsellProperties } from '@theme/upsell-tracking';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #stickyUpdateFrame = 0;

  /** @type {Set<string>} */
  #upsellRequests = new Set();

  #drawerMutationObserver = new MutationObserver(() => this.#queueStickyUpdate());

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener('click', this.#handleUpsellNavigation);
    this.addEventListener('click', this.#handleUpsellAdd);
    if (this.#dialog) {
      this.#drawerMutationObserver.observe(this.#dialog, { childList: true, subtree: true });
    }

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener('click', this.#handleUpsellNavigation);
    this.removeEventListener('click', this.#handleUpsellAdd);
    this.#drawerMutationObserver.disconnect();
    if (this.#stickyUpdateFrame) cancelAnimationFrame(this.#stickyUpdateFrame);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => this.#queueStickyUpdate();

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #queueStickyUpdate = () => {
    if (this.#stickyUpdateFrame) cancelAnimationFrame(this.#stickyUpdateFrame);

    this.#stickyUpdateFrame = requestAnimationFrame(() => {
      this.#stickyUpdateFrame = 0;
      this.#updateStickyState();
    });
  };

  #handleUpsellNavigation = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-cart-upsell-direction]');
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    const shell = button.closest('[data-cart-upsell-shell]');
    const track = shell?.querySelector('[data-cart-upsell-track]');
    const slide = track?.querySelector('[data-cart-upsell-slide]');
    if (!(track instanceof HTMLElement) || !(slide instanceof HTMLElement)) return;

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = slide.getBoundingClientRect().width + gap;
    const direction = button.dataset.cartUpsellDirection === 'previous' ? -1 : 1;
    track.scrollBy({ left: distance * direction, behavior: 'smooth' });
  };

  #handleUpsellAdd = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-cart-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || button.disabled) return;

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const requestedDiscountPercent = Number(button.dataset.discountPercent);
    const discountPercent =
      Number.isFinite(requestedDiscountPercent) && requestedDiscountPercent > 0 ? requestedDiscountPercent : 0;
    if (!variantId) return;

    if (this.#upsellRequests.has(variantId)) return;
    this.#upsellRequests.add(variantId);

    button.disabled = true;
    const label = button.querySelector('[data-cart-upsell-add-label]');
    if (label) label.textContent = 'Adding...';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_SoleSteady add-on]', 'Frequently Bought Together');
    if (discountPercent > 0) {
      formData.append('properties[_SoleSteady discount]', `${discountPercent}% automatic`);
    }
    appendUpsellProperties(formData, {
      source: button.dataset.upsellSource,
      placement: button.dataset.upsellPlacement,
      offer: button.dataset.upsellOffer,
      parent: button.dataset.upsellParent,
      version: button.dataset.upsellVersion,
      discountPercent,
    });

    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    }
    formData.append('sections', Array.from(sectionIds).join(','));
    formData.append('sections_url', window.location.pathname);

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const fetchOptions = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchOptions,
        headers: { ...fetchOptions.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Unable to refresh the cart');

      const cart = await cartResponse.json();
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          productId,
          sections: result.sections,
          source: 'cart-drawer-frequently-bought-together',
          didError: false,
        },
      });
    } catch (error) {
      deferredPromise.reject(error);
      button.disabled = false;
      if (label) label.textContent = '+ Add';

      const liveRegion = this.querySelector('[data-cart-upsell-live]');
      if (liveRegion) liveRegion.textContent = error?.message || 'Unable to add this item';

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Unable to add this item',
          code: 'INVALID',
        })
      );
    } finally {
      this.#upsellRequests.delete(variantId);
    }
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    if (content.querySelector('[data-cart-upsell]')) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
